import { useState, useCallback } from 'react';
import { validateEmail, validateRequired } from '../utils/validation';

export const useContact = () => {
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [errors, setErrors] = useState({});

  const updateContactData = useCallback((updates) => {
    setContactData(prev => ({ ...prev, ...updates }));
    
    // Clear errors for updated fields
    const newErrors = { ...errors };
    Object.keys(updates).forEach(key => {
      delete newErrors[key];
    });
    setErrors(newErrors);
  }, [errors]);

  const validateForm = useCallback(() => {
    const validationErrors = {};

    // Required field validation
    if (!validateRequired(contactData.name)) {
      validationErrors.name = 'Name is required';
    }

    if (!validateRequired(contactData.email)) {
      validationErrors.email = 'Email is required';
    } else if (!validateEmail(contactData.email)) {
      validationErrors.email = 'Please enter a valid email address';
    }

    if (!validateRequired(contactData.message)) {
      validationErrors.message = 'Message is required';
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [contactData]);

  const submitForm = useCallback(async () => {
    if (!validateForm()) {
      return false;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const result = {
        success: true,
        messageId: `MSG${Date.now()}`,
        sender: {
          name: contactData.name,
          email: contactData.email,
          phone: contactData.phone
        },
        subject: contactData.subject || 'General Inquiry',
        message: contactData.message,
        timestamp: new Date().toISOString(),
        expectedResponse: '24 hours'
      };

      setSubmitResult(result);
      return result;
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [contactData, validateForm]);

  const resetForm = useCallback(() => {
    setContactData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    setSubmitResult(null);
    setErrors({});
    setIsSubmitting(false);
  }, []);

  const canSubmit = useCallback(() => {
    return !!(
      contactData.name.trim() && 
      contactData.email.trim() && 
      validateEmail(contactData.email) &&
      contactData.message.trim()
    );
  }, [contactData]);

  const getCharacterCount = useCallback((field) => {
    return contactData[field]?.length || 0;
  }, [contactData]);

  const isFieldValid = useCallback((field) => {
    switch (field) {
      case 'name':
        return validateRequired(contactData.name);
      case 'email':
        return validateRequired(contactData.email) && validateEmail(contactData.email);
      case 'message':
        return validateRequired(contactData.message);
      default:
        return true;
    }
  }, [contactData]);

  return {
    // State
    contactData,
    isSubmitting,
    submitResult,
    errors,
    
    // Actions
    updateContactData,
    submitForm,
    resetForm,
    validateForm,
    
    // Helpers
    canSubmit,
    getCharacterCount,
    isFieldValid
  };
};